import DecisioningAuth from './decisioningAuth';
import Actions from 'webapp/actions/actions';
import LocationHelper from 'webapp/helpers/location_helper';

export default class DecisioningServiceClient {
  constructor() {
    const decisioningAuth = new DecisioningAuth();
    this.auth = decisioningAuth;
    this.jdcsBaseUrl = decisioningAuth.jdcsBaseUrl;
  }

  /**
   * Returns true if currentProcessVer >= expectedProcessVer, false otherwise
   */
  checkProcessVersion = (currentProcessVer, expectedProcessVer) => {
    if (currentProcessVer == null) {
      return false;
    }
    return currentProcessVer.localeCompare(expectedProcessVer, undefined, { numeric: true, sensitivity: 'base' }) > -1;
  };

  get url() {
    return this.jdcsBaseUrl;
  }

  getCurrentUsername = () => this.auth.quickRead(this.auth.idToken)?.email;

  async doBackendCall(path, method = 'GET', rawBody = null, type = 'json') {
    if (!this.auth.isLoggedIn()) {
      return this.auth.onAuthError();
    }

    let quickReadToken = this.auth.quickRead(this.auth.idToken);
    if (quickReadToken.exp < Date.now() / 1000) {
      this.auth.idToken = null;
      return this.auth.onAuthError();
    }

    if (this.auth.userHasNoGroups()) {
      Actions.setRoute('/pending-cognito-auth');
      return Actions.setView({ page: 'pendingCognitoAuth' });
    }

    try {
      let response = await fetch(`${this.jdcsBaseUrl}/rest/v1${path}`, {
        method,
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: this.auth.idToken,
        },
        body: rawBody !== null && typeof rawBody === 'object' ? JSON.stringify(rawBody) : rawBody,
      });
      if (response.status === 401) {
        console.log('error on response', response);
        return this.auth.onAuthError(response);
      }
      return type === 'json' ? response.json() : response.text();
    } catch (err) {
      throw err;
    }
  }

  getUsers = async () => this.doBackendCall('/users');

  getEntry = async () => this.doBackendCall('/entry');

  changeOwner = async ({ id, decision }) => this.doBackendCall(`/decisions/${id}`, 'PATCH', decision);

  reapply = async ({ id }) => this.doBackendCall(`/decisions/${id}/reapply`, 'POST');

  hardenCredit = async ({ id }) => this.doBackendCall(`/decisions/${id}/hardenCredit`, 'POST');

  adhocStip = async ({ id, stipId, code, text }) =>
    this.doBackendCall(`/decisions/${id}/adhocStip`, 'POST', { id: stipId, code, text });

  overrideCreditStatus = async ({ id, creditStatusOverride = null, denialReasonOverrides = {} }) =>
    this.doBackendCall(`/decisions/${id}/overrideCreditStatus`, 'POST', {
      creditStatusOverride,
      denialReasonOverrides,
    });

  fetchDecisionNotes = async ({ caseId }) => this.doBackendCall(`/decisions/${caseId}/decisionNote`, 'GET');

  createDecisionNotes = async ({ caseId, body }) =>
    this.doBackendCall(`/decisions/${caseId}/decisionNote`, 'POST', body);

  deleteDecisionNote = async ({ caseId, id }) =>
    this.doBackendCall(`/decisions/${caseId}/decisionNote/${id}`, 'DELETE');

  getDecisions = async () => this.doBackendCall('/decisions');

  getDecisionsInactive = async () =>
    this.doBackendCall(`/decisions?inactive=false&search=${LocationHelper.searchAsObj().search || 'all'}`);

  getDecision = async (id) => this.doBackendCall(`/decisions/${id}`);

  getUserByCustomerId = async (id) => this.doBackendCall(`/decisions/users/customer/${id}`);
  getSwitchDataByCustomerId = async (id) => this.doBackendCall(`/decisions/customers/${id}/data`);

  saveDecision = async (decision) => this.doBackendCall(`/decisions/${decision.id}`, 'PUT', decision);

  deleteDocument = async (documentId) => this.doBackendCall(`/documents/${documentId}`, 'DELETE');

  getApplicantPII = async (id, applicantType) =>
    (await this.doBackendCall(`/decisions/${id}/applicantPII?applicantType=${applicantType}`))?.[0];

  getTuXml = async (filename, id) => this.doBackendCall(`/tuXml/${filename}?caseId=${id}`, 'GET', null, 'xml');

  getProcessDenialReasons = async (id) => this.doBackendCall(`/decisions/${id}/processDenialReasons`);
}
